import { useEffect, useState } from "react";
import { ShieldOff, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Tiny inline opt-out toggle for the technician's job editor.
 * Surfaces a clear banner when the customer is currently opted out
 * (so the team doesn't try to "send SMS now" only for it to silently fail).
 */
export default function CustomerOptOutToggle({ phone }) {
  const [opted, setOpted] = useState(null); // null = loading, true/false = known
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!phone) { setOpted(false); return; }
    setOpted(null);
    api.get(`/sms/opt-outs/${encodeURIComponent(phone)}`)
      .then((r) => setOpted(!!r.data.opted_out))
      .catch(() => setOpted(false));
  }, [phone]);

  if (!phone) return null;
  if (opted === null) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (opted) {
        await api.delete(`/sms/opt-outs/${encodeURIComponent(phone)}`);
        toast.success("SMS re-enabled for this customer");
        setOpted(false);
      } else {
        await api.post("/sms/opt-outs", { phone, reason: "Customer requested via technician" });
        toast.success("SMS opt-out saved — every future message to this number is blocked");
        setOpted(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update opt-out");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs transition-colors ${
        opted
          ? "border-warning/40 bg-warning/[0.08] text-warning hover:bg-warning/[0.15]"
          : "border-border bg-muted/10 text-muted-foreground hover:text-foreground hover:bg-muted/20"
      }`}
      data-testid="customer-opt-out-toggle"
    >
      <span className="inline-flex items-center gap-2">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : opted ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
        {opted ? "Customer has opted out of SMS" : "SMS allowed (PECR)"}
      </span>
      <span className="font-mono-plex text-[10px] opacity-80">
        {opted ? "Tap to re-enable" : "Tap to opt out"}
      </span>
    </button>
  );
}
